/**
 * Context Bridge Component
 * 
 * This component allows step components that use useQuestionnaireForm() hook
 * to work seamlessly with EditorFormContext. It consumes EditorFormContext
 * and provides its values through QuestionnaireFormContext.
 * 
 * This enables code reuse: step components remain unchanged but can work
 * in both Questionnaire and Editor modes.
 */

import React, { ReactNode } from 'react'
import { useEditorForm } from './EditorFormContext'
import { FormContextType } from '../../common/context/SharedFormTypes'
import { QuestionnaireFormContext } from '../../questionnaire/context/QuestionnaireFormContext'

interface EditorFormContextBridgeProps {
    children: ReactNode
}

/**
 * Bridge component that makes EditorFormContext values available
 * through QuestionnaireFormContext, enabling step component reuse
 */
export const EditorFormContextBridge = ({ children }: EditorFormContextBridgeProps) => {
    const editorContext = useEditorForm()
    
    // Convert EditorFormContextType to FormContextType
    // (remove editor-specific fields like hasUnsavedChanges, saveForm, markAsSaved)
    const bridgedContext: FormContextType = {
        formData: editorContext.formData,
        storageContext: editorContext.storageContext,
        updateBasicInfo: editorContext.updateBasicInfo,
        updateMainImages: editorContext.updateMainImages,
        updateCeremonyLocation: editorContext.updateCeremonyLocation,
        updateWeddingLocation: editorContext.updateWeddingLocation,
        updateGuestsFAQ: editorContext.updateGuestsFAQ,
        addFAQItem: editorContext.addFAQItem,
        updateFAQAnswer: editorContext.updateFAQAnswer,
        removeFAQItem: editorContext.removeFAQItem,
        updateWitnesses: editorContext.updateWitnesses,
        updateContact: editorContext.updateContact,
        updateDomain: editorContext.updateDomain,
        updateMenu: editorContext.updateMenu,
        updateSchedule: editorContext.updateSchedule,
        updateColors: editorContext.updateColors,
        updateTables: editorContext.updateTables,
        updateWeddingDetails: editorContext.updateWeddingDetails,
        updateCurrentStep: editorContext.updateCurrentStep,
        loadFormData: editorContext.loadFormData,
        resetForm: editorContext.resetForm,
        markImageChanged: editorContext.markImageChanged
    }
    
    return (
        <QuestionnaireFormContext.Provider value={bridgedContext}>
            {children}
        </QuestionnaireFormContext.Provider>
    )
}
